import { Injectable, inject } from '@angular/core';
import { ADPResourceService, ListItem } from 'digital-core';
import { Observable } from 'rxjs';
import { AddressValidationResponseDto } from 'src/app/model/account/address-validation-response.dto';

export interface MappedAddress {
  address1: string;
  city: string | null;
  state: string | null;
  zipCode: string;
}

/**
 * Pure/stateless address-entry logic shared across the components that let a
 * customer type or autocomplete a US address (confirm-info today; onboarding's
 * product-registration/address-lookup are follow-up migrations). Lifecycle
 * concerns (subscription teardown, form wiring) stay in each component.
 */
@Injectable({ providedIn: 'root' })
export class AddressEntryService {
  private resourceService = inject(ADPResourceService);

  /** CMS state list for `country` (defaults to the `Global_Country` portal setting), sorted with a blank option prepended. */
  loadStates$(country?: string): Observable<ListItem[]> | null {
    const countryValue = country ?? this.resourceService.getPortalSetting('Global_Country')?.Value;
    return this.resourceService.getListItemResource('Global ' + countryValue + ' States');
  }

  /** Sorts a raw CMS state list, backfills `Value = Key`, and prepends a blank option — call once per `loadStates$` emission. */
  sortStates(states: ListItem[]): ListItem[] {
    const sorted = states.sort((a, b) => a.Key.trim().replace(/&nbsp;/g, '').toLowerCase().localeCompare(b.Key.trim().replace(/&nbsp;/g, '').toLowerCase()));
    sorted.splice(0, 0, new ListItem('', null, null));
    sorted.forEach((state) => {
      state.Value = state.Key;
    });
    return sorted;
  }

  /**
   * Parses a Google Places `PlaceResult` into the address1/city/state/zipCode shape.
   * Pass the text the customer typed as `typedInput` so an apt/suite/unit they entered
   * survives the selection — Google's components only carry street number + route.
   */
  mapPlaceResult(place: google.maps.places.PlaceResult, typedInput?: string | null): MappedAddress {
    let streetNumber: string | undefined;
    let streetName = '';
    let postalCode = '';
    let city: string | null = null;
    let state: string | null = null;

    place.address_components?.forEach((value) => {
      if (value.types.indexOf('street_number') > -1) {
        streetNumber = value.short_name;
      }
      if (value.types.indexOf('route') > -1) {
        streetName = value.long_name;
      }
      if (value.types.indexOf('postal_code') > -1) {
        // Keep the ZIP as text. Parsing it as a Number corrupts two common Google
        // payloads while still leaving the field looking filled in: a ZIP+4
        // ("33157-1234") becomes NaN, and leading zeros are dropped ("00501" → 501).
        // Both then fail the 5-digit CMS pattern, and because the resource has a
        // single ValidationMessage every failure reads "ZIP code is required".
        postalCode = (value.short_name ?? '').replace(/\D/g, '').substring(0, 5);
      }
      if (value.types.indexOf('locality') > -1) {
        city = value.long_name;
      }
      if (value.types.indexOf('administrative_area_level_1') > -1) {
        state = value.short_name;
      }
    });

    let address1 = typeof streetNumber === 'undefined' ? streetName : `${streetNumber} ${streetName}`;

    const unitNumber = this.extractUnitNumber(typedInput);
    if (unitNumber && !address1.toLowerCase().includes(unitNumber.toLowerCase())) {
      address1 = `${address1} ${unitNumber}`;
    }

    return { address1, city, state, zipCode: postalCode };
  }

  /**
   * Pulls an apt/suite/unit designator ("Suite 100", "Apt 4B", "#12") out of free text.
   * Keywords are whole-word matched so street names like "Steele" or "Captain" don't count.
   */
  extractUnitNumber(input?: string | null): string | null {
    if (!input) {
      return null;
    }
    const unitPattern = /(?:\b(?:apt|apartment|unit|suite|ste|bldg|building|room|rm)\b\.?|#)\s*#?\s*[\w-]+/i;
    const match = input.match(unitPattern);
    return match ? match[0].trim() : null;
  }

  /**
   * Branches on `validate-address`'s response — same order/keys as
   * address-setup.component.ts's `validateAddress()`. Returns the CMS label key for a
   * validation failure, or `null` when the address validated successfully.
   *
   * Uses the generic `AddressEntry_Validation_Code_*` labels (duplicated from the
   * onboarding-only `Signup_Address_Validation_Code_*` set at the HW parent level so
   * this non-signup consumer isn't semantically coupled to the signup flow's copy).
   */
  getValidationFailureLabelKey(resp: AddressValidationResponseDto): string | null {
    if (Object.keys(resp).length === 0) {
      return 'AddressEntry_Validation_Code_UnableToValidate';
    }
    if (resp.error?.ruleName === 'AddressServiceNotAvailable') {
      return 'AddressEntry_Validation_Code_AddressServiceNotAvailable';
    }
    if (resp.error) {
      return 'AddressEntry_Validation_Code_InternalError';
    }
    if (resp.formattedAddress?.statusCode) {
      return 'AddressEntry_Validation_Code_' + resp.formattedAddress.statusCode;
    }
    if (resp.formattedAddress?.recordType === 'PostOfficeBox') {
      return 'AddressEntry_Validation_Code_PostOfficeBox';
    }
    return null;
  }
}
